import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { RightOutlined, DownOutlined } from '@ant-design/icons'
import LayoutPartner from '../../components/BasicComponent/LayoutPartner'
import FixedBottom from '../../components/BasicComponent/FixedBottom'
import SafeImage from '../../components/BasicComponent/SafeImage'
import iconSuccess from '../../assets/images/payment_success.png'
import { CUSTOMER_RECEIPT_STATUS_TO_TEXT } from '../../constants/global'
import { ORDER_PAYMENT_STATUS_COLOR, PAYMENT_FAILED, PAYMENT_SUCCESS } from '../../constants/payment'
import { PATH } from '../../constants/router'
import { number_to_price } from '../../helper/common'
import { checkDurationUsageDate } from '../../helper/formatDate'
import appUserService from '../../services/appUserService'
import './index.scss'

// Tach services (unique theo productId) va vehicles (unique theo plate) tu orderItems.
function extractServicesAndVehiclesInOrderDetail(items) {
  const serviceMap = new Map()
  const plateSet = new Set()
  const vehicles = []

  for (const orderItem of items) {
    const key = orderItem?.productId
    if (key && !serviceMap.has(key)) {
      serviceMap.set(key, { ...orderItem })
    }

    let plates = []

    try {
      const orderItemVehicle = JSON.parse(orderItem?.orderItemVehicle || '{}')
      const vehicleIdentity = orderItemVehicle?.vehicleIdentity
      if (Array.isArray(vehicleIdentity)) {
        plates = vehicleIdentity
      } else if (typeof vehicleIdentity === 'string') {
        plates = [vehicleIdentity]
      }
    } catch (_) {
      // ignore parse error
    }

    if (plates.length === 0) {
      const rawValue = orderItem?.orderItemValue
      let parsedValue = rawValue

      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim()
        const looksLikeJson =
          (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
          (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('"') && trimmed.endsWith('"'))

        if (looksLikeJson) {
          try {
            parsedValue = JSON.parse(trimmed)
          } catch (_) {
            parsedValue = rawValue
          }
        }
      }

      if (Array.isArray(parsedValue)) {
        plates = parsedValue
      } else if (typeof parsedValue === 'string') {
        plates = [parsedValue]
      }
    }

    for (const plate of plates) {
      if (typeof plate !== 'string') continue
      const normalized = plate.trim()
      if (!normalized || plateSet.has(normalized)) continue
      plateSet.add(normalized)
      vehicles.push({ plate: normalized })
    }
  }

  return {
    services: [...serviceMap.values()],
    vehicles
  }
}

export default function AutomatedTrafficFineNotificationSuccess() {
  const history = useHistory()
  const { id: orderId } = useParams()

  const [orderDetail, setOrderDetail] = useState(null)
  const [services, setServices] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [showAllVehicles, setShowAllVehicles] = useState(false)

  useEffect(() => {
    if (!orderId) {
      return undefined
    }

    let isActive = true

    const fetchOrderDetail = async () => {
      try {
        const orderResult = await appUserService.getOrderDetail({ id: orderId })

        if (!isActive || orderResult?.statusCode !== 200 || !orderResult?.data) {
          return
        }

        const detail = orderResult.data
        const { services: extractedServices, vehicles: extractedVehicles } = extractServicesAndVehiclesInOrderDetail(detail?.orderItems || [])

        setOrderDetail(detail)
        setServices(extractedServices)
        setVehicles(extractedVehicles)
      } catch {
        // bo qua loi mang, view giu nguyen trang thai trong.
      }
    }

    fetchOrderDetail()

    return () => {
      isActive = false
    }
  }, [orderId])

  const displayedVehicles = useMemo(() => (showAllVehicles ? vehicles : vehicles.slice(0, 1)), [showAllVehicles, vehicles])

  const orderStatusTitle = useMemo(() => {
    if (orderDetail?.paymentStatus === PAYMENT_SUCCESS) {
      return 'thành công'
    }
    if (orderDetail?.paymentStatus === PAYMENT_FAILED) {
      return 'thất bại'
    }
    return ''
  }, [orderDetail?.paymentStatus])

  const orderStatus = CUSTOMER_RECEIPT_STATUS_TO_TEXT[orderDetail?.paymentStatus?.toUpperCase()] || ''
  const colorStatus = ORDER_PAYMENT_STATUS_COLOR.find((item) => item.value === orderDetail?.paymentStatus)?.color

  return (
    <LayoutPartner>
      <div className="AutomatedTrafficFineNotificationSuccess">
        <div className="AutomatedTrafficFineNotificationSuccess_image">
          <SafeImage src={iconSuccess} />
        </div>
        <div className="AutomatedTrafficFineNotificationSuccess_total">
          <div className="total_label">Mua gói thông báo phạt nguội {orderStatusTitle}</div>
          <div className="total_value">
            <strong>{number_to_price(orderDetail?.totalAmount || 0)}</strong>đ
          </div>
        </div>
        <div className="AutomatedTrafficFineNotificationSuccess_divider" />
        <div className="AutomatedTrafficFineNotificationSuccess_content">
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Nội dung</div>
            <div className="text_value">Mua gói thông báo phạt nguội</div>
          </div>
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Gói dịch vụ</div>
            <div className="text_value">{services[0]?.productName || '-'}</div>
          </div>
          {/* Thông tin xe */}
          {displayedVehicles.map((vehicle, index) => (
            <div key={index} className="AutomatedTrafficFineNotificationSuccess_item">
              <div className="text_color_secondary">Thông tin xe {vehicles.length > 1 && (showAllVehicles || index > 0) ? `(${index + 1})` : ''}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="text_value">{vehicle.plate || '-'}</span>
                {vehicles.length > 1 &&
                  index === 0 &&
                  (showAllVehicles ? (
                    <DownOutlined style={{ cursor: 'pointer', marginLeft: '8px' }} onClick={() => setShowAllVehicles(false)} />
                  ) : (
                    <RightOutlined style={{ cursor: 'pointer', marginLeft: '8px' }} onClick={() => setShowAllVehicles(true)} />
                  ))}
              </div>
            </div>
          ))}
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Thời hạn</div>
            <div className="text_value">{checkDurationUsageDate(services[0]?.usageDate || 30)}</div>
          </div>
        </div>
        <div className="AutomatedTrafficFineNotificationSuccess_divider" />
        <div className="AutomatedTrafficFineNotificationSuccess_content">
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Số tiền</div>
            <div className="text_value">{number_to_price(orderDetail?.subTotalAmount || 0)}đ</div>
          </div>
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Thuế VAT (10%)</div>
            <div className="text_value">{number_to_price(orderDetail?.taxAmount || 0)}đ</div>
          </div>
          {orderDetail?.discountAmount > 0 && (
            <div className="AutomatedTrafficFineNotificationSuccess_item">
              <div className="text_color_secondary">Mã giảm giá</div>
              <div className="text_value">{number_to_price(orderDetail?.discountAmount || 0)}đ</div>
            </div>
          )}
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Tổng tiền thanh toán</div>
            <div className="text_value">{number_to_price(orderDetail?.totalAmount || 0)}đ</div>
          </div>
        </div>
        <div className="AutomatedTrafficFineNotificationSuccess_divider" />
        <div className="AutomatedTrafficFineNotificationSuccess_content">
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Trạng thái</div>
            <div className="text_bold text_value" style={{ fontWeight: 'bold', color: colorStatus || 'var(--text-primary)' }}>
              {orderStatus}
            </div>
          </div>
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Mã giao dịch</div>
            <div className="text_bold text_value">{orderDetail?.orderCode || orderDetail?.orderData?.orderCode || '-'}</div>
          </div>
          <div className="AutomatedTrafficFineNotificationSuccess_item">
            <div className="text_color_secondary">Thời gian</div>
            <div className="text_bold text_value">
              {orderDetail?.updatedAt || orderDetail?.orderData?.updatedAt
                ? new Date(orderDetail?.updatedAt || orderDetail?.orderData?.updatedAt).toLocaleString('vi-VN')
                : '-'}
            </div>
          </div>
        </div>
        <div className="AutomatedTrafficFineNotificationSuccess_divider" />
        <FixedBottom elementPaddingBottom={'LayoutPartner'}>
          <div
            className="AutomatedTrafficFineNotificationSuccess_continue"
            onClick={() => {
              history.push(PATH.TRAFFIC_FINE_SERVICES)
            }}>
            Tiếp tục mua gói
          </div>
        </FixedBottom>
      </div>
    </LayoutPartner>
  )
}
